import { bind } from 'decko'
import { LiveNodeList } from 'live-node-list'
import {
  CartError,
  CartItemError,
  AddToCartError,
  UpdateCartError,
  ProductNotInCartError,
  NotEnoughStockCartItemError,
  NotEnoughStockCartError,
  ProductNotFoundCartError,
  InvalidDiscountCodeError,
  DiscountCodeFormError,
} from './cart-errors'
import {
  CartErrorEvent,
  CartItemErrorEvent,
  AddToCartEvent,
  RemoveFromCartEvent,
  UpdateCartEvent,
  CartProcessingEndEvent,
  CartProcessingStartEvent,
  CartDiscountRemovedEvent,
  CartDiscountAppliedEvent,
} from './cart-events'

const { root } = window.Shopify.routes

export default class CartApi {
  /**
   * @type {LiveNodeList}
   */
  addToCartForms = new LiveNodeList(`form[action="${root}cart/add"]`)

  constructor() {
    this.registerListeners()
  }

  registerListeners() {
    this.addToCartForms.addEventListener('submit', this.handleSubmit)
  }

  @bind
  async handleSubmit(event) {
    event.preventDefault()
    event.stopPropagation()

    const form = event.currentTarget || event.target
    const formData = new FormData(form)

    const { id, quantity, ...data } = Object.fromEntries(formData.entries())
    for (const key in data) {
      const value = data[key]

      const matches = key.match(/properties\[([^\]]+)\]/)
      if (matches) {
        const [, property] = matches

        if (!data.properties) {
          data.properties = {}
        }

        data.properties[property] = value
        delete data[key]
      }
    }

    return this.addToCart(id, quantity, data)
  }

  @bind
  async handleCartApiCall(callback, lineId) {
    try {
      return await callback()
    } catch (error) {
      if (!(error instanceof CartError)) {
        if (lineId) {
          const lineItem = await this.getCartItem(lineId)
          error = new CartItemError(error.message, lineItem)
        } else {
          error = new CartError(error.message)
        }
      }

      let event = new CartErrorEvent({ error })
      if (error instanceof CartItemError) {
        event = new CartItemErrorEvent({ error, lineItem: error.lineItem })
      }

      document.dispatchEvent(event)
    }
  }

  @bind
  async getCart() {
    const response = await fetch(`${root}cart.js`)
    return response.json()
  }

  @bind
  async getCartItem(lineId) {
    const cart = await this.getCart()
    return { ...cart.items[lineId - 1], lineId }
  }

  @bind
  async getCartVariant(variantId) {
    const cart = await this.getCart()
    return cart.items.find(item => item.variant_id === variantId)
  }

  @bind
  async addToCart(variantId, quantity = 1, data = {}) {
    document.dispatchEvent(new CartProcessingStartEvent())
    this.handleCartApiCall(async () => {
      try {
        const body = { id: variantId, quantity, ...data }
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })
        const responseData = await response.json()

        if (!response.ok) {
          if (responseData.status === 422) {
            throw new NotEnoughStockCartError(responseData.description)
          }
          if (responseData.status === 404) {
            throw new ProductNotFoundCartError(responseData.description)
          }

          throw new AddToCartError(undefined, responseData.description)
        }

        document.dispatchEvent(new AddToCartEvent({ lineItem: responseData }))
        document.dispatchEvent(
          new CartProcessingEndEvent({ lineItem: responseData }),
        )
      } catch (error) {
        if (error instanceof CartError) {
          throw error
        }

        throw new AddToCartError(error.message)
      }
    })
  }

  @bind
  async updateCart(data) {
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const responseData = await response.json()

      if (!response.ok) {
        const lineItem = await this.getCartItem(data.line)

        if (responseData.status === 422) {
          throw new NotEnoughStockCartItemError(
            responseData.description,
            lineItem,
          )
        }
        if (responseData.status === 404) {
          throw new ProductNotInCartError(responseData.description, lineItem)
        }

        throw new UpdateCartError(responseData.description, lineItem)
      }

      return responseData
    } catch (error) {
      if (error instanceof CartError) {
        throw error
      }

      const lineItem = await this.getCartItem(data.line)
      throw new UpdateCartError(error.message, lineItem)
    }
  }

  @bind
  async updateLineItem(lineId, quantity) {
    if (quantity < 1) {
      return this.removeLineItem(lineId)
    }

    let lineItem = await this.getCartItem(lineId)
    document.dispatchEvent(new CartProcessingStartEvent({ lineItem }))

    return this.handleCartApiCall(async () => {
      const cart = await this.updateCart({ line: lineId, quantity })
      if (cart.items_added[0] || cart.items_removed[0]) {
        lineItem = cart.items_added[0] || cart.items_removed[0]
      }

      const event = new UpdateCartEvent({
        lineItem,
        cart,
      })
      document.dispatchEvent(event)
      document.dispatchEvent(new CartProcessingEndEvent({ lineItem }))
    }, lineId)
  }

  @bind
  async updateSellingPlan(lineId, sellingPlanId) {
    const lineItem = await this.getCartItem(lineId)
    document.dispatchEvent(new CartProcessingStartEvent({ lineItem }))

    return this.handleCartApiCall(async () => {
      const cart = await this.updateCart({
        line: lineId,
        selling_plan: sellingPlanId,
      })
      const lineItem = cart.items[lineId - 1]

      const event = new UpdateCartEvent({
        lineItem,
        cart,
      })
      document.dispatchEvent(event)
      document.dispatchEvent(new CartProcessingEndEvent({ lineItem }))
    })
  }

  @bind
  async removeLineItem(lineId) {
    const lineItem = await this.getCartItem(lineId)
    document.dispatchEvent(new CartProcessingStartEvent({ lineItem }))

    return this.handleCartApiCall(async () => {
      const cart = await this.updateCart({ line: lineId, quantity: 0 })
      const lineItem = cart.items_removed[0]

      const event = new RemoveFromCartEvent({
        lineItem,
        cart,
      })
      document.dispatchEvent(event)
      document.dispatchEvent(new CartProcessingEndEvent({ lineItem }))
    }, lineId)
  }

  @bind
  async getCartDiscounts(forCustomer = false) {
    const cart = await this.getCart()
    const discounts = cart.items.reduce((acc = {}, item) => {
      item.line_level_discount_allocations
        .filter(({ discount_application }) =>
          forCustomer
            ? discount_application.type === 'discount_code'
            : discount_application.type !== 'discount_code',
        )
        .forEach(discount => {
          const { key, title } = discount.discount_application
          if (!(key in acc)) {
            acc[key] = {
              title,
              amount: 0,
            }
          }

          acc[key].amount += discount.amount
        })

      return acc
    }, {})

    cart.cart_level_discount_applications
      .filter(item =>
        forCustomer
          ? item.type === 'discount_code'
          : item.type !== 'discount_code',
      )
      .forEach(discount => {
        const { key, title } = discount
        if (!(key in discounts)) {
          discounts[key] = {
            title,
            amount: 0,
          }
        }

        discounts[key].amount += discount.total_allocated_amount
      })

    return discounts
  }

  async getCustomerDiscounts() {
    return this.getCartDiscounts(true)
  }

  @bind
  async applyDiscount(code, silent = false) {
    return this.handleCartApiCall(async () => {
      const response = await fetch(`${root}discount/${code}`)

      if (!response.ok) {
        throw new DiscountCodeFormError()
      }

      if (silent) {
        return
      }

      const cart = await this.getCart()
      const discountApplication =
        cart.cart_level_discount_applications.find(
          discount => discount.title.toLowerCase() === code.toLowerCase(),
        ) ||
        cart.items.find(
          item =>
            !!item.line_level_discount_allocations.find(
              ({ discount_application }) =>
                discount_application.type === 'discount_code' &&
                discount_application.title.toLowerCase() === code.toLowerCase(),
            ),
        )

      if (!discountApplication) {
        throw new InvalidDiscountCodeError()
      }

      const event = new CartDiscountAppliedEvent({
        code,
        cart,
        discountApplication,
      })
      document.dispatchEvent(event)
    })
  }

  @bind
  async removeDiscount() {
    // We can remove the discount code by applying a random, invalid code, silently
    return this.handleCartApiCall(async () => {
      await this.applyDiscount('77020ce6-f651-4b08-97d1-69afcce0c2e4', true)

      document.dispatchEvent(new CartDiscountRemovedEvent())
    })
  }
}
